import { useRef } from 'react'

import { useTabOwnerId } from '@/components/layout/SheetContainerContext'
import { useCloseOwners, type CloseOwner } from '@/stores/closeOwners'
import { useMountEffect } from '@/hooks/useMountEffect'

/**
 * Registers the calling editor as its tab's close owner, so the close
 * coordinator can offer "Save" when the tab has unsaved work.
 *
 * Outside a tab tree (`useTabOwnerId()` is null) this is a no-op.
 *
 * Registration is mount-scoped: the tab id is fixed for the lifetime of the
 * component (each tab is a keyed `<Activity>` subtree), and the registered
 * object is a stable proxy that forwards to the latest `owner`. That keeps
 * inline owner objects free and makes the unregister a plain unmount cleanup —
 * no re-registration churn, no raw effect.
 */
export function useWorkspaceCloseOwner(owner: CloseOwner): void {
  const tabId = useTabOwnerId()

  const ownerRef = useRef(owner)
  ownerRef.current = owner

  const proxy = useRef<CloseOwner>({
    save: () => ownerRef.current.save(),
  }).current

  useMountEffect(() => {
    if (!tabId) return
    const store = useCloseOwners.getState()
    store.register(tabId, proxy)
    return () => store.unregister(tabId, proxy)
  })
}
